/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import Styles from "./Offers.module.scss";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../../shared/styles/useTheme";
import classNames from "classnames";
import { gasOffer, electricityOffer, MyShortOffersBaseRoute, heatpumpOffer } from "@/routes/offer";
import { Button } from "@App/components/@remote";
import { Link } from "react-router-dom";
import { Icon } from "@/App/components/@remote";

type Item = {
  icon: React.ReactNode[];
  name: string | React.ReactNode;
};

type Props = {
  footertext: string;
  items: Item[];
  className?: string;
  links: string[];
  shortReminders: Remote.ChangeService[];
};

type ItemsProps = {
  items: Item[];
  className?: string;
};

export const Items = ({ items, className }: ItemsProps) => {
  return (
    <>
      {items.map((item: Item, i) => (
        <div key={`item_${i}`} className={classNames(Styles.itemWrapper, className)}>
          {React.Children.map(item.icon, (icon) => {
            if (React.isValidElement(icon) && icon.type && (icon.type as any).displayName === "Icon") {
              return React.cloneElement(icon as any, { "aria-hidden": "true" } as any);
            }
            return icon;
          })}
          <p className={Styles.itemName}>{item.name}</p>
        </div>
      ))}
    </>
  );
};

export const Offers = ({ items, className, shortReminders = [] }: Props) => {
  const { t } = useTranslation();
  const { link } = useTheme();
  const hasShortReminders = shortReminders.length > 0;

  const title = hasShortReminders
    ? t("dashboard.shortReminders.title.shortReminders")
    : t("dashboard.shortReminders.title.noShortReminders");

  const subTitle = hasShortReminders
    ? t("dashboard.shortReminders.description.shortReminders")
    : t("dashboard.shortReminders.description.noShortReminders");

  const OffersButton = ({ text, link, classname }: { text: string; link: string; classname?: string }) => {
    return (
      <Link to={link} className={Styles.btnContent} aria-label={text}>
        <Button className={classNames(Styles.button, classname)} tabIndex={-1}>
          {text} <Icon className={Styles.arrow} name={"arrowRight"} aria-hidden="true" />
        </Button>
      </Link>
    );
  };

  const boxContent = hasShortReminders ? (
    <>
      <OffersButton classname={Styles.boxButton} link={MyShortOffersBaseRoute} text={t("dashboard.seeOffers")} />
      <div>
        <div className={Styles.upperShadow}></div>
        <div className={Styles.footer}>
          <p>
            <b>{t("dashboard.shortReminders.newOffer")}</b>
          </p>
          <>
            <div className={Styles.footerLinks}>
              <Link to={gasOffer} className={link} aria-label={t("dashboard.gas")}>
                <span>{t("dashboard.gas")}</span>{" "}
                <Icon className={Styles.arrow} name={"arrowRight"} aria-hidden="true" />
              </Link>
              <Link to={electricityOffer} className={link} aria-label={t("dashboard.electricity")}>
                <span>{t("dashboard.electricity")}</span>{" "}
                <Icon className={Styles.arrow} name={"arrowRight"} aria-hidden="true" />
              </Link>
              <Link to={heatpumpOffer} className={link} aria-label={t("dashboard.heatPump")}>
                <span>{t("dashboard.heatPump")}</span>{" "}
                <Icon className={Styles.arrow} name={"arrowRight"} aria-hidden="true" />
              </Link>
            </div>
          </>
        </div>
      </div>
    </>
  ) : (
    <div className={Styles.getOfferWrapper}>
      <h4 className={classNames(Styles.getOffer, "oswald")}>{t("dashboard.createOffer")}</h4>
      <div className={Styles.boxButtons}>
        <OffersButton link={electricityOffer} text="Strom" />
        <OffersButton link={gasOffer} text="Gas" />
      </div>
    </div>
  );

  return (
    <div className={classNames(Styles.dashboardBoxWrapper, className)}>
      <div className={Styles.upperBox}>
        <h2 className={classNames(Styles.title, "oswald")}>{title}</h2>
        <p className={Styles.subTitle}>{subTitle}</p>

        <Items className={Styles.items} items={items} />
        {boxContent}
      </div>
    </div>
  );
};
